import React, { useCallback, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import classnames from "classnames";
import { selectors, actions } from "@shared/store";
import { MODAL_TYPES } from "@shared/constants";
import { StationIssuesConfirmModal } from "@containers/Dashboard/components";
import { ModalSize } from "@shared/interfaces";
import { useDraggable, useResize } from "@shared/hooks";
import { SensorDataSessionMarkerModal } from "@containers/SensorDataSession/components/SensorDataSessionDetailsContainer/SensorDataSessionChartDotsList/components";
import { SensorDataRestoringRequestRestoreModal } from "@containers/SensorDataRestoringRequest/components/SensorDataRestoringRequestListContainer/";
import { CreateDotMarkerModal } from "@containers/SensorDataSession/components/SensorDataSessionDetailsContainer/SensorDataSessionChart/components";
import { SelectFileModal } from "@containers/SensorDataSession/components/SensorDataSessionCreateContainer/SensorDataSessionForm/components";
import { AlertReadConfirmModal } from "@containers/Alert/components/";
import { SensorDataSessionDateRangeModal } from "@containers/SensorDataSession/components";

import { ImportModal } from "../ImportModal";
import { ModalConfirm } from "../ModalConfirm";
import { InactionModal } from "../InactionModal";
import { ImportResultsModal } from "../ImportResultsModal";
import { InfoModal } from "../InfoModal";

import "./index.scss";

const ModalWrapper = () => {
  const dispatch = useDispatch();
  const dragElementRef = useRef<HTMLDivElement>(null);
  const modalElementRef = useRef<HTMLDivElement>(null);
  const resizerElementRef = useRef<HTMLDivElement>(null);

  const { dragging } = useDraggable(dragElementRef, modalElementRef);
  useResize(resizerElementRef, modalElementRef);

  const modal = useSelector(selectors.getModal());

  const onClose = useCallback(() => {
    if (modal?.props.onClose) {
      modal.props.onClose();
    }

    dispatch(actions.hideModal());
  }, [modal, dispatch]);

  const onSuccess = useCallback(
    (comment?: string) => {
      if (modal?.props.onSuccess) {
        modal.props.onSuccess(comment);
      }

      dispatch(actions.hideModal());
    },
    [modal, dispatch],
  );

  const Component = useMemo(() => {
    if (!modal) return;
    return modalStrategy(modal.type);
  }, [modal]);

  return modal ? (
    <>
      {!modal.draggable && <div className="dynamic-modal-overlay" />}
      <div
        className={classnames("dynamic-modal-wrapper", {
          "dynamic-modal-wrapper_draggable": modal.draggable,
        })}
      >
        <div
          ref={modalElementRef}
          className={classnames("dynamic-modal", "box", `dynamic-modal_size-${modal.size || ModalSize.NORMAL}`, {
            "dynamic-modal_dragging": dragging,
          })}
        >
          {modal.draggable && (
            <div ref={dragElementRef} className="dynamic-modal-drag-panel">
              <span className="dynamic-modal-drag-panel-dot" />
              <span className="dynamic-modal-drag-panel-dot" />
              <span className="dynamic-modal-drag-panel-dot" />
            </div>
          )}
          <div className="dynamic-modal-content">
            {Component ? <Component {...modal.props} onClose={onClose} onSuccess={onSuccess} /> : null}
          </div>
          {modal.resizable && (
            <div ref={resizerElementRef} className="dynamic-modal-resizer">
              ⟷
            </div>
          )}
        </div>
      </div>
    </>
  ) : null;
};

export default ModalWrapper;

const modalStrategy = (type: string) => {
  switch (type) {
    case MODAL_TYPES.CONFIRM: {
      return ModalConfirm;
    }
    case MODAL_TYPES.INFO: {
      return InfoModal;
    }
    case MODAL_TYPES.SENSOR_DATA_RESTORE: {
      return SensorDataRestoringRequestRestoreModal;
    }
    case MODAL_TYPES.CREATE_MARKER_FORM: {
      return SensorDataSessionMarkerModal;
    }
    case MODAL_TYPES.ISSUE_READ_CONFIRM: {
      return StationIssuesConfirmModal;
    }
    case MODAL_TYPES.IMPORT_RESULTS: {
      return ImportResultsModal;
    }
    case MODAL_TYPES.CREATE_DOT_FORM: {
      return CreateDotMarkerModal;
    }
    case MODAL_TYPES.SELECT_FILE_FORM: {
      return SelectFileModal;
    }
    case MODAL_TYPES.INACTION_MODAL: {
      return InactionModal;
    }
    case MODAL_TYPES.ALERT_READ_CONFIRM: {
      return AlertReadConfirmModal;
    }
    case MODAL_TYPES.IMPORT_PATIENTS: {
      return ImportModal;
    }
    case MODAL_TYPES.SENSOR_DATA_SESSION_SELECT_DATE_RANGE: {
      return SensorDataSessionDateRangeModal;
    }
  }
};
